import { useCallback, useEffect, useState } from "react";
import {
  getOpsOwnershipDigestPreview,
  patchOpsOwnershipDigestSchedule,
  postOpsOwnershipDigestSend,
} from "@/lib/opsPaperworkClient";
import { priorCalendarDayUtc } from "@/lib/messages/ownershipDigestModel";

type Props = {
  digestEnabled: boolean;
  onToggleEnabled: (v: boolean) => Promise<void>;
  scheduleEncouragingNote: string;
  scheduleLocalSendTime: string;
  lastSentAt: string | null;
  lastDigestDay: string | null;
};

export function OpsOwnershipDigestSection(props: Props) {
  const [reportDay, setReportDay] = useState(() => priorCalendarDayUtc());
  const [encouragingNote, setEncouragingNote] = useState(props.scheduleEncouragingNote);
  const [localSendTime, setLocalSendTime] = useState(props.scheduleLocalSendTime);
  const [previewBody, setPreviewBody] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [emailCopy, setEmailCopy] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    setEncouragingNote(props.scheduleEncouragingNote);
    setLocalSendTime(props.scheduleLocalSendTime);
  }, [props.scheduleEncouragingNote, props.scheduleLocalSendTime]);

  const loadPreview = useCallback(async () => {
    setBusy(true);
    setMsg("");
    const r = await getOpsOwnershipDigestPreview(reportDay);
    setBusy(false);
    if (!r.ok) {
      setMsg(r.error);
      setPreviewBody("");
      setPreviewTitle("");
      setEmailCopy("");
      return;
    }
    setPreviewTitle(r.data.preview.title);
    setPreviewBody(r.data.preview.body);
    setEmailCopy(r.data.email_copy_stub ?? r.data.preview.plain_text);
  }, [reportDay]);

  useEffect(() => {
    if (props.digestEnabled) void loadPreview();
  }, [props.digestEnabled, loadPreview]);

  const saveSchedule = async () => {
    setBusy(true);
    setMsg("");
    const r = await patchOpsOwnershipDigestSchedule({
      encouraging_note: encouragingNote.trim() || null,
      local_send_time: localSendTime.trim() || null,
    });
    setBusy(false);
    if (!r.ok) {
      setMsg(r.error);
      return;
    }
    setMsg("Schedule saved.");
    await loadPreview();
  };

  const sendNow = async (force = false) => {
    setBusy(true);
    setMsg("");
    const r = await postOpsOwnershipDigestSend({ report_day: reportDay, force });
    setBusy(false);
    if (!r.ok) {
      setMsg(r.error);
      return;
    }
    setMsg(`Sent to ${r.data.recipient_count} member(s).`);
    setPreviewTitle(r.data.preview.title);
    setPreviewBody(r.data.preview.body);
    setEmailCopy(r.data.email_copy_stub ?? r.data.preview.plain_text);
  };

  const copyEmailStub = async () => {
    if (!emailCopy) return;
    try {
      await navigator.clipboard.writeText(emailCopy);
      setMsg("Email copy stub copied to clipboard.");
    } catch {
      setMsg("Could not copy — select the preview text manually.");
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-gray-600 dark:text-gray-400 leading-snug">
        Morning ownership summary in-app — prior-day sent leads, MTD leaderboard snippet, and your encouraging note.
        Delivers as system notifications (not DM threads). Scheduled cron is a follow-up; use <strong>Send now</strong> for
        pilot.
      </p>

      <label className="flex items-center gap-2 text-sm text-gray-900 dark:text-gray-100">
        <input
          type="checkbox"
          checked={props.digestEnabled}
          onChange={(e) => void props.onToggleEnabled(e.target.checked)}
        />
        Enable ownership digest for this org
      </label>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-[11px] text-gray-700 dark:text-gray-300 block">
          Report day (prior-day sales)
          <input
            type="date"
            className="mt-0.5 w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-zinc-900"
            value={reportDay}
            onChange={(e) => setReportDay(e.target.value)}
          />
        </label>
        <label className="text-[11px] text-gray-700 dark:text-gray-300 block">
          Scheduled send time (HH:mm, future cron)
          <input
            type="text"
            placeholder="07:00"
            className="mt-0.5 w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-zinc-900 font-mono"
            value={localSendTime}
            onChange={(e) => setLocalSendTime(e.target.value)}
          />
        </label>
      </div>

      <label className="text-[11px] text-gray-700 dark:text-gray-300 block">
        Encouraging note template
        <textarea
          className="mt-0.5 w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-zinc-900 min-h-[3rem]"
          value={encouragingNote}
          onChange={(e) => setEncouragingNote(e.target.value)}
          maxLength={2000}
        />
      </label>

      {props.lastSentAt ? (
        <p className="text-[10px] text-gray-500">
          Last sent: {new Date(props.lastSentAt).toLocaleString()}
          {props.lastDigestDay ? ` · report day ${props.lastDigestDay}` : ""}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <button type="button" disabled={busy} className="rounded border px-3 py-1 text-sm" onClick={() => void saveSchedule()}>
          Save schedule
        </button>
        <button type="button" disabled={busy || !props.digestEnabled} className="rounded border px-3 py-1 text-sm" onClick={() => void loadPreview()}>
          Refresh preview
        </button>
        <button
          type="button"
          disabled={busy || !props.digestEnabled}
          className="rounded bg-indigo-600 text-white px-3 py-1 text-sm disabled:opacity-50"
          onClick={() => void sendNow(false)}
        >
          Send now
        </button>
        <button type="button" disabled={!emailCopy} className="rounded border px-3 py-1 text-sm" onClick={() => void copyEmailStub()}>
          Copy email stub
        </button>
      </div>

      {msg ? <p className="text-[11px] text-gray-700 dark:text-gray-300">{msg}</p> : null}

      {previewTitle ? (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-zinc-950/50 px-3 py-2 text-[11px] whitespace-pre-wrap">
          <p className="font-semibold mb-1">{previewTitle}</p>
          {previewBody}
        </div>
      ) : null}
    </div>
  );
}
